<template>
  <div />
</template>

<script setup lang="ts">
import type { Position } from 'geojson'
import type {
  Map as MapLibreMap,
  GeoJSONSourceSpecification,
  GeoJSONSource,
  LineLayerSpecification
} from 'maplibre-gl'

const props = withDefaults(
  defineProps<{
    coordinates: Position[]
    map: MapLibreMap
    cursor?: string
    lineColor?: string
    lineWidth?: number
    lineDasharray?: number[]
    borderLineColor?: string
    usesShippedRouteSource?: boolean
    interactive?: boolean
  }>(),
  {
    cursor: 'pointer',
    lineWidth: 3,
    lineColor: undefined,
    borderLineColor: '#fff',
    usesShippedRouteSource: false,
    interactive: true
  }
)

// Source shipped inside the new RISER map styles, pre-styled with the style's
// own tuned route colors. When the active style provides it we only feed it
// geometry and let the style draw the visible line.
const SHIPPED_ROUTE_SOURCE_ID = 'trip-route-geometry'

const emit = defineEmits([
  'mouseenter',
  'mousemove',
  'mouseleave',
  'mousedown',
  'click',
  'dblclick',
  'drag'
])

const state = reactive<{
  source: GeoJSONSource | null
  mapStyleSheetId: string | null
}>({
  source: null,
  mapStyleSheetId: null
})

const layerID = computed(() => {
  const id = Math.round(Math.random() * 1000000)

  return `route${id}`
})

const attemptRemoveLayers = () => {
  // map can be null when this runs after the parent destroyed it (e.g. deferred styledata/idle callback)
  if (!props.map) return

  if (props.map.getLayer(layerID.value + '-cursor')) {
    props.map.removeLayer(layerID.value + '-cursor')
  }

  if (props.map.getLayer(layerID.value + '-border')) {
    props.map.removeLayer(layerID.value + '-border')
  }

  if (props.map.getLayer(layerID.value)) {
    props.map.removeLayer(layerID.value)
  }

  if (props.map.getSource(layerID.value)) {
    props.map.removeSource(layerID.value)
  }

  // Clear the style-owned source so its pre-styled line doesn't linger after
  // this route is torn down (the source outlives our own layers).
  if (props.usesShippedRouteSource) {
    const shippedSource = props.map.getSource<GeoJSONSource>(SHIPPED_ROUTE_SOURCE_ID)

    shippedSource?.setData({ type: 'FeatureCollection', features: [] })
  }
}

const getFeatureFromRoute = () => {
  const feature: GeoJSON.Feature<GeoJSON.LineString> = {
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: props.coordinates
    },
    properties: {}
  }

  return feature
}

const createSource = () => {
  const source: GeoJSONSourceSpecification = {
    type: 'geojson',
    data: getFeatureFromRoute()
  }

  props.map.addSource(layerID.value, source)

  const updatedSource = props.map.getSource<GeoJSONSource>(layerID.value)

  if (updatedSource) {
    // @ts-expect-error - not sure why this causes a "type instantiation is excessively deep" error
    state.source = updatedSource
  }
}

const bindEvents = () => {
  props.map.on('mouseenter', layerID.value + '-cursor', (event) => {
    props.map.getCanvas().style.cursor = props.cursor
    emit('mouseenter', event)
  })

  props.map.on('mousemove', layerID.value + '-cursor', (event) => {
    props.map.getCanvas().style.cursor = props.cursor
    emit('mousemove', event)
  })

  props.map.on('mouseleave', layerID.value + '-cursor', () => {
    props.map.getCanvas().style.cursor = ''
    emit('mouseleave', event)
  })

  props.map.on('mousedown', layerID.value + '-cursor', (event) => {
    emit('mousedown', event)
  })

  props.map.on('click', layerID.value + '-cursor', (event) => {
    emit('click', event)
  })

  props.map.on('dblclick', layerID.value + '-cursor', (event) => {
    emit('dblclick', event)
  })

  // @ts-expect-error - not sure why drag is not recognized here
  props.map.on('drag', layerID.value + '-cursor', (event) => {
    emit('drag', event)
  })
}

const addLayer = () => {
  const layers = props.map.getStyle().layers

  const firstSymbolLayer = layers.find((layer) => layer.type === 'symbol')

  const referenceLayer = layers.find((layer) => layer.id === 'reference-layer')

  const beforeId = referenceLayer?.id ?? firstSymbolLayer?.id

  // extra-wide transparent layer that provides the hit area for hover/click
  if (props.interactive) {
    const cursorLayer: LineLayerSpecification = {
      id: layerID.value + '-cursor',
      type: 'line',
      source: layerID.value,
      paint: {
        'line-width': props.lineWidth + 15,
        'line-color': 'transparent'
      }
    }

    props.map.addLayer(cursorLayer, beforeId)
  }

  // The style ships its own pre-styled route line; we only fill its source
  // (see fillShippedSource) and skip drawing our own visible line on top.
  if (props.usesShippedRouteSource) return

  const riserBlue = `rgb(${getComputedStyle(document.documentElement).getPropertyValue('--color-blue').trim()})`

  const borderLayer: LineLayerSpecification = {
    id: layerID.value + '-border',
    type: 'line',
    source: layerID.value,
    paint: {
      'line-width': props.lineWidth + 3,
      'line-color': props.borderLineColor
    }
  }

  props.map.addLayer(borderLayer, beforeId)

  const paint: LineLayerSpecification['paint'] = {
    'line-width': props.lineWidth,
    'line-color': props.lineColor ?? riserBlue
  }

  if (props.lineDasharray) {
    paint['line-dasharray'] = props.lineDasharray
  }

  const layer: LineLayerSpecification = {
    id: layerID.value,
    type: 'line',
    source: layerID.value,
    paint,
    layout: {
      'line-cap': 'round'
    }
  }
  props.map.addLayer(layer, beforeId)
}

const fillShippedSource = () => {
  if (!props.usesShippedRouteSource || !props.map) return

  const source = props.map.getSource<GeoJSONSource>(SHIPPED_ROUTE_SOURCE_ID)

  source?.setData(getFeatureFromRoute())
}

const updateRoute = () => {
  state.source?.setData(getFeatureFromRoute())

  fillShippedSource()
}

const reDrawRoute = () => {
  attemptRemoveLayers()
  createSource()
  addLayer()
  fillShippedSource()
}

const styleDataChangeHandler = (newMap: MapLibreMap) => {
  // @ts-expect-error - id is not typed
  if (state.mapStyleSheetId === newMap.style.stylesheet.id) {
    return
  }

  props.map.once('idle', reDrawRoute)

  // @ts-expect-error - id is not typed
  state.mapStyleSheetId = newMap.style.stylesheet.id
}

onMounted(() => {
  reDrawRoute()

  if (props.interactive) {
    bindEvents()
  }

  // @ts-expect-error - id is not typed
  state.mapStyleSheetId = props.map.style.stylesheet.id

  props.map.on('styledata', styleDataChangeHandler)
})

onBeforeUnmount(() => {
  props.map.off('styledata', styleDataChangeHandler)

  attemptRemoveLayers()
})

watch(
  () => props.coordinates,
  () => {
    updateRoute()
  },
  {
    deep: true
  }
)
</script>
